/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  DiscoveryIndex,
  NetworkInterface,
  Subnet,
  RouteTable,
  NetworkAcl,
  SecurityGroup,
  VPC,
  InternetGateway,
  NatGateway,
  TransitGatewayAttachment,
  VPCPeeringConnection,
} from './types';

/**
 * Build lookup indexes from discovery.json data for fast traffic analysis.
 * Supports both AWS (vpcs) and OCI (vcns) discovery formats.
 */
export function buildDiscoveryIndex(discovery: any): DiscoveryIndex {
  const index: DiscoveryIndex = {
    eniMap: new Map(),
    ipToEni: new Map(),
    subnetMap: new Map(),
    cidrToSubnet: [],
    routeTableMap: new Map(),
    subnetToRouteTable: new Map(),
    naclMap: new Map(),
    subnetToNacl: new Map(),
    securityGroupMap: new Map(),
    vpcMap: new Map(),
    vpcCidrs: [],
    igwMap: new Map(),
    natGwMap: new Map(),
    tgwAttachmentMap: new Map(),
    vpcPeeringMap: new Map(),
  };

  if (!discovery) return index;

  // Determine if AWS or OCI format
  const isOCI = !!discovery.vcns && !discovery.vpcs;

  if (isOCI) {
    indexOCIDiscovery(discovery, index);
  } else {
    indexAWSDiscovery(discovery, index);
  }

  return index;
}

// =============================================================================
// AWS DISCOVERY INDEXING
// =============================================================================

function indexAWSDiscovery(discovery: any, index: DiscoveryIndex): void {
  const vpcs = discovery.vpcs || {};

  for (const [vpcId, vpcData] of Object.entries<any>(vpcs)) {
    const info = vpcData.info || vpcData;

    // Index VPC
    const vpcEntry: VPC = {
      id: vpcId,
      cidr_block: info.cidr_block || info.CidrBlock || '',
      tags: info.tags || info.Tags || {},
    };
    index.vpcMap.set(vpcId, vpcEntry);

    if (vpcEntry.cidr_block) {
      index.vpcCidrs.push(vpcEntry.cidr_block);
    }

    // Also add secondary CIDRs if present
    if (info.cidr_block_association_set) {
      for (const assoc of info.cidr_block_association_set) {
        if (assoc.CidrBlock && assoc.CidrBlock !== vpcEntry.cidr_block) {
          index.vpcCidrs.push(assoc.CidrBlock);
        }
      }
    }

    // Index subnets
    const subnets = vpcData.subnets || info.subnets || [];
    for (const subnet of subnets) {
      const subnetEntry: Subnet = {
        id: subnet.id || subnet.SubnetId,
        cidr_block: subnet.cidr_block || subnet.CidrBlock,
        vpc_id: vpcId,
        availability_zone: subnet.availability_zone || subnet.AvailabilityZone,
        tags: subnet.tags || subnet.Tags || {},
      };
      index.subnetMap.set(subnetEntry.id, subnetEntry);

      if (subnetEntry.cidr_block) {
        index.cidrToSubnet.push({
          cidr: subnetEntry.cidr_block,
          subnetId: subnetEntry.id,
          vpcId,
        });
      }
    }

    // Index route tables and their subnet associations
    const routeTables = vpcData.route_tables || info.route_tables || [];
    for (const rt of routeTables) {
      const rtEntry: RouteTable = {
        id: rt.id || rt.RouteTableId,
        vpc_id: vpcId,
        routes: rt.routes || rt.Routes || [],
        associations: rt.associations || rt.Associations || [],
        tags: rt.tags || rt.Tags || {},
      };
      index.routeTableMap.set(rtEntry.id, rtEntry);

      // Map subnets to their route table
      for (const assoc of rtEntry.associations) {
        if (assoc.SubnetId) {
          index.subnetToRouteTable.set(assoc.SubnetId, rtEntry.id);
        }
      }
    }

    // Index NACLs and their subnet associations
    const nacls = vpcData.network_acls || info.network_acls || [];
    for (const nacl of nacls) {
      const naclEntry: NetworkAcl = {
        id: nacl.id || nacl.NetworkAclId,
        vpc_id: vpcId,
        entries: nacl.entries || nacl.Entries || [],
        associations: nacl.associations || nacl.Associations || [],
        tags: nacl.tags || nacl.Tags || {},
      };
      index.naclMap.set(naclEntry.id, naclEntry);

      // Map subnets to their NACL
      for (const assoc of naclEntry.associations) {
        if (assoc.SubnetId) {
          index.subnetToNacl.set(assoc.SubnetId, naclEntry.id);
        }
      }
    }

    // Index security groups
    const securityGroups = vpcData.security_groups || info.security_groups || [];
    for (const sg of securityGroups) {
      const sgEntry: SecurityGroup = {
        id: sg.id || sg.GroupId,
        group_name: sg.group_name || sg.GroupName,
        description: sg.description || sg.Description,
        vpc_id: vpcId,
        ip_permissions: sg.ip_permissions || sg.IpPermissions || [],
        ip_permissions_egress: sg.ip_permissions_egress || sg.IpPermissionsEgress || [],
        tags: sg.tags || sg.Tags || {},
      };
      index.securityGroupMap.set(sgEntry.id, sgEntry);
    }

    // Index ENIs and IP mappings
    const networkInterfaces = vpcData.network_interfaces || info.network_interfaces || [];
    for (const eni of networkInterfaces) {
      // Extract security group IDs from various formats
      const sgIds: string[] = [];
      
      // Format 1: security_groups as array of strings
      if (eni.security_groups && Array.isArray(eni.security_groups)) {
        for (const sg of eni.security_groups) {
          if (typeof sg === 'string') {
            sgIds.push(sg);
          } else if (sg?.GroupId) {
            sgIds.push(sg.GroupId);
          } else if (sg?.group_id) {
            sgIds.push(sg.group_id);
          }
        }
      }
      
      // Format 2: Groups array (AWS SDK format)
      if (eni.Groups && Array.isArray(eni.Groups)) {
        for (const g of eni.Groups) {
          const gId = typeof g === 'string' ? g : (g?.GroupId || g?.group_id);
          if (gId && !sgIds.includes(gId)) {
            sgIds.push(gId);
          }
        }
      }
      
      // Format 3: groups (lowercase)
      if (eni.groups && Array.isArray(eni.groups)) {
        for (const g of eni.groups) {
          const gId = typeof g === 'string' ? g : (g?.GroupId || g?.group_id);
          if (gId && !sgIds.includes(gId)) {
            sgIds.push(gId);
          }
        }
      }
      
      console.log('[DiscoveryIndex] ENI', eni.id || eni.NetworkInterfaceId, 'security groups:', sgIds);
      
      const eniEntry: NetworkInterface = {
        id: eni.id || eni.NetworkInterfaceId,
        subnet_id: eni.subnet_id || eni.SubnetId,
        vpc_id: vpcId,
        private_ip_address: eni.private_ip_address || eni.PrivateIpAddress,
        private_ip_addresses: eni.private_ip_addresses || eni.PrivateIpAddresses || [],
        security_groups: sgIds,
        description: eni.description || eni.Description,
      };
      index.eniMap.set(eniEntry.id, eniEntry);

      // Map private IPs to ENI
      if (eniEntry.private_ip_address) {
        index.ipToEni.set(eniEntry.private_ip_address, eniEntry.id);
      }
      for (const ipInfo of eniEntry.private_ip_addresses || []) {
        const ip = ipInfo.PrivateIpAddress || (ipInfo as any).private_ip_address;
        if (ip) {
          index.ipToEni.set(ip, eniEntry.id);
        }
      }
    }

    // Index Internet Gateways
    const igws = vpcData.internet_gateways || info.internet_gateways || [];
    for (const igw of igws) {
      const igwEntry: InternetGateway = {
        id: igw.id || igw.InternetGatewayId,
        tags: igw.tags || igw.Tags || {},
      };
      index.igwMap.set(igwEntry.id, igwEntry);
    }

    // Index NAT Gateways
    const natGws = vpcData.nat_gateways || info.nat_gateways || [];
    for (const natgw of natGws) {
      const natEntry: NatGateway = {
        id: natgw.id || natgw.NatGatewayId,
        subnet_id: natgw.subnet_id || natgw.SubnetId,
        nat_gateway_addresses: natgw.nat_gateway_addresses || natgw.NatGatewayAddresses || [],
        tags: natgw.tags || natgw.Tags || {},
      };
      index.natGwMap.set(natEntry.id, natEntry);
    }

    // Index Transit Gateway Attachments
    const tgwAttachments = vpcData.transit_gateway_attachments || info.transit_gateway_attachments || [];
    for (const tgwAttach of tgwAttachments) {
      const tgwEntry: TransitGatewayAttachment = {
        id: tgwAttach.id || tgwAttach.TransitGatewayAttachmentId,
        transit_gateway_id: tgwAttach.transit_gateway_id || tgwAttach.TransitGatewayId,
        vpc_id: vpcId,
      };
      index.tgwAttachmentMap.set(tgwEntry.id, tgwEntry);
    }

    // Index VPC Peering Connections
    const peeringConnections = vpcData.peering_connections || info.peering_connections || [];
    for (const pcx of peeringConnections) {
      const pcxEntry: VPCPeeringConnection = {
        id: pcx.id || pcx.VpcPeeringConnectionId,
      };
      index.vpcPeeringMap.set(pcxEntry.id, pcxEntry);
    }
  }
}

// =============================================================================
// OCI DISCOVERY INDEXING (for future support)
// =============================================================================

function indexOCIDiscovery(discovery: any, index: DiscoveryIndex): void {
  const vcns = discovery.vcns || {};

  for (const [vcnId, vcnData] of Object.entries<any>(vcns)) {
    // OCI VCN -> VPC equivalent
    const vpcEntry: VPC = {
      id: vcnId,
      cidr_block: vcnData.cidr_block || vcnData.cidr_blocks?.[0] || '',
      tags: { Name: vcnData.display_name || vcnData.name || vcnId },
    };
    index.vpcMap.set(vcnId, vpcEntry);

    if (vpcEntry.cidr_block) {
      index.vpcCidrs.push(vpcEntry.cidr_block);
    }

    // OCI Subnets
    const subnets = vcnData.subnets || [];
    for (const subnet of subnets) {
      const subnetEntry: Subnet = {
        id: subnet.id,
        cidr_block: subnet.cidr_block,
        vpc_id: vcnId,
        availability_zone: subnet.availability_domain,
        tags: { Name: subnet.display_name || subnet.name },
      };
      index.subnetMap.set(subnetEntry.id, subnetEntry);

      if (subnetEntry.cidr_block) {
        index.cidrToSubnet.push({
          cidr: subnetEntry.cidr_block,
          subnetId: subnetEntry.id,
          vpcId: vcnId,
        });
      }
    }

    // OCI Route Tables
    const routeTables = vcnData.route_tables || [];
    for (const rt of routeTables) {
      // Convert OCI route rules to AWS-like format
      const routes = (rt.route_rules || []).map((rule: any) => ({
        DestinationCidrBlock: rule.destination || rule.destination_cidr_block,
        GatewayId: rule.network_entity_id,
        State: 'active',
      }));

      const rtEntry: RouteTable = {
        id: rt.id,
        vpc_id: vcnId,
        routes,
        associations: [], // OCI associates via subnet's route_table_id
        tags: { Name: rt.display_name || rt.name },
      };
      index.routeTableMap.set(rtEntry.id, rtEntry);
    }

    // Map OCI subnets to their route tables
    for (const subnet of subnets) {
      if (subnet.route_table_id) {
        index.subnetToRouteTable.set(subnet.id, subnet.route_table_id);
      }
    }

    // OCI Security Lists -> NACL-like
    const securityLists = vcnData.security_lists || [];
    for (const sl of securityLists) {
      const entries: any[] = [];

      // Ingress rules
      for (const rule of sl.ingress_security_rules || []) {
        entries.push({
          RuleNumber: entries.length + 1,
          RuleAction: 'allow',
          Protocol: rule.protocol || '-1',
          CidrBlock: rule.source || '0.0.0.0/0',
          Egress: false,
          PortRange: rule.tcp_options?.destination_port_range || rule.udp_options?.destination_port_range,
        });
      }

      // Egress rules
      for (const rule of sl.egress_security_rules || []) {
        entries.push({
          RuleNumber: entries.length + 1,
          RuleAction: 'allow',
          Protocol: rule.protocol || '-1',
          CidrBlock: rule.destination || '0.0.0.0/0',
          Egress: true,
          PortRange: rule.tcp_options?.destination_port_range || rule.udp_options?.destination_port_range,
        });
      }

      const naclEntry: NetworkAcl = {
        id: sl.id,
        vpc_id: vcnId,
        entries,
        associations: [],
        tags: { Name: sl.display_name || sl.name },
      };
      index.naclMap.set(naclEntry.id, naclEntry);
    }

    // Map OCI subnets to their security lists
    for (const subnet of subnets) {
      if (subnet.security_list_ids && subnet.security_list_ids[0]) {
        index.subnetToNacl.set(subnet.id, subnet.security_list_ids[0]);
      }
    }

    // OCI Network Security Groups -> Security Group equivalent
    const nsgs = vcnData.network_security_groups || [];
    for (const nsg of nsgs) {
      const inboundRules: any[] = [];
      const outboundRules: any[] = [];

      for (const rule of nsg.security_rules || []) {
        const sgRule = {
          IpProtocol: rule.protocol || '-1',
          FromPort: rule.tcp_options?.destination_port_range?.min,
          ToPort: rule.tcp_options?.destination_port_range?.max,
          IpRanges: rule.source ? [{ CidrIp: rule.source }] : [{ CidrIp: rule.destination }],
        };

        if (rule.direction === 'INGRESS') {
          inboundRules.push(sgRule);
        } else {
          outboundRules.push(sgRule);
        }
      }

      const sgEntry: SecurityGroup = {
        id: nsg.id,
        group_name: nsg.display_name || nsg.name,
        description: nsg.description,
        vpc_id: vcnId,
        ip_permissions: inboundRules,
        ip_permissions_egress: outboundRules,
        tags: { Name: nsg.display_name || nsg.name },
      };
      index.securityGroupMap.set(sgEntry.id, sgEntry);
    }

    // OCI VNICs -> ENI equivalent
    const vnics = vcnData.vnics || vcnData.network_interfaces || [];
    for (const vnic of vnics) {
      const eniEntry: NetworkInterface = {
        id: vnic.id,
        subnet_id: vnic.subnet_id,
        vpc_id: vcnId,
        private_ip_address: vnic.private_ip,
        private_ip_addresses: vnic.private_ips?.map((ip: any) => ({ PrivateIpAddress: ip.ip_address })) || [],
        security_groups: vnic.nsg_ids || [],
        description: vnic.display_name || vnic.hostname_label,
      };
      index.eniMap.set(eniEntry.id, eniEntry);

      if (eniEntry.private_ip_address) {
        index.ipToEni.set(eniEntry.private_ip_address, eniEntry.id);
      }
    }

    // OCI Internet Gateway
    const igws = vcnData.internet_gateways || [];
    for (const igw of igws) {
      index.igwMap.set(igw.id, {
        id: igw.id,
        tags: { Name: igw.display_name || igw.name },
      });
    }

    // OCI NAT Gateway
    const natGws = vcnData.nat_gateways || [];
    for (const natgw of natGws) {
      index.natGwMap.set(natgw.id, {
        id: natgw.id,
        subnet_id: natgw.subnet_id || '',
        nat_gateway_addresses: natgw.nat_ip ? [{ PublicIp: natgw.nat_ip }] : [],
        tags: { Name: natgw.display_name || natgw.name },
      });
    }

    // OCI DRG (Dynamic Routing Gateway) -> TGW equivalent
    const drgs = vcnData.drgs || vcnData.drg_attachments || [];
    for (const drg of drgs) {
      index.tgwAttachmentMap.set(drg.id, {
        id: drg.id,
        transit_gateway_id: drg.drg_id,
        vpc_id: vcnId,
      });
    }
  }
}

/**
 * Find the subnet that contains a given IP address
 */
export function findSubnetForIP(ip: string, index: DiscoveryIndex): Subnet | undefined {
  for (const { cidr, subnetId } of index.cidrToSubnet) {
    // Use local CIDR check
    const [cidrIp, prefixStr] = cidr.split('/');
    const prefix = parseInt(prefixStr, 10);
    if (isNaN(prefix)) continue;

    const ipInt = ipToIntLocal(ip);
    const cidrInt = ipToIntLocal(cidrIp);
    const mask = prefix === 0 ? 0 : ((0xFFFFFFFF << (32 - prefix)) >>> 0);

    if ((ipInt & mask) === (cidrInt & mask)) {
      return index.subnetMap.get(subnetId);
    }
  }
  return undefined;
}

function ipToIntLocal(ip: string): number {
  const parts = ip.split('.');
  if (parts.length !== 4) return 0;
  return parts.reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
}

